import { useState, useEffect } from "react";
import axios from "axios";
import { sendEmail } from "../../utils";
import { getActiveEmailTemplate, getQueuedEmailTemplate } from "../../emailTemplates";

import { parseCancellationsFromActivities } from "../../subscriptionUtils";
import { formatTastePreference } from "../../tastePreferences";

// Mirrors the backend's purchaseOverlapsActiveSubs (subscriptionController.js) —
// a purchase only needs to queue if it shares a meal-type track with an
// already-active plan (e.g. buying dinner while only lunch is active does NOT
// overlap and can activate immediately, running alongside the existing plan).
const getMealCoverage = (sub) => ({
  lunch: (sub.lunchMeals || 0) + (sub.nextDayLunchMeals || 0) > 0,
  dinner: (sub.dinnerMeals || 0) + (sub.nextDayDinnerMeals || 0) > 0,
});
const getPurchaseCoverage = (lunchDinner) => ({
  lunch: lunchDinner === "lunch" || lunchDinner === "lunchAndDinner",
  dinner: lunchDinner === "dinner" || lunchDinner === "lunchAndDinner",
});
export const purchaseOverlapsActiveSubs = (lunchDinner, activeSubs) => {
  const newCoverage = getPurchaseCoverage(lunchDinner);
  const activeLunch = activeSubs.some((s) => getMealCoverage(s).lunch);
  const activeDinner = activeSubs.some((s) => getMealCoverage(s).dinner);
  return (newCoverage.lunch && activeLunch) || (newCoverage.dinner && activeDinner);
};

export const useSubscription = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [currentPlans, setCurrentPlans] = useState([]);
  const [nextPlan, setNextPlan] = useState(null);
  const [hasQueuedPlan, setHasQueuedPlan] = useState(false);
  const [cancellations, setCancellations] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");

      if (!userId || !token) {
        setIsLoading(false);
        return;
      }

      try {
        const [response, holidaysRes, activitiesRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}subscription/${userId}/subscription`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          ),
          axios.get(`${process.env.REACT_APP_API_URL}holiday/get-holidays`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: { holidays: [] } })),
          axios.get(`${process.env.REACT_APP_API_URL}activity/get-activities?userId=${userId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => ({ data: [] })),
        ]);

        setIsSubscribed(response.data.isSubscribed);
        if (response.data.isSubscribed) {
          // Prefer the explicit currentPlan key; fall back to legacy 'subscription' key
          setCurrentPlan(response.data.currentPlan || response.data.subscription);
          setCurrentPlans(response.data.currentPlans || []);
        } else {
          setCurrentPlans([]);
        }
        if (response.data.nextPlan) {
          setNextPlan(response.data.nextPlan);
          setHasQueuedPlan(true);
        } else {
          setNextPlan(null);
          setHasQueuedPlan(false);
        }

        const rawCancellations = response.data.cancellations || [];
        const activityCancellations = parseCancellationsFromActivities(activitiesRes.data || []);
        setCancellations([...rawCancellations, ...activityCancellations]);
        setHolidays(holidaysRes.data?.holidays || response.data.holidays || []);
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, []);

  const handleSubscribe = async (
    planName,
    mealCount,
    price,
    mealType,
    carbType,
    lunchDinner,
    mealStartDate,
    allergy,
    taste,
    setErrorMessage,
  ) => {
    // Only block on an existing queued plan if THIS purchase would also need to
    // queue (i.e. it overlaps an active plan's meal-type coverage) — a genuinely
    // non-overlapping purchase (e.g. dinner bought while only lunch is active)
    // is allowed to go active immediately regardless of an unrelated queued plan.
    const wouldOverlap = purchaseOverlapsActiveSubs(lunchDinner, currentPlans);
    if (hasQueuedPlan && wouldOverlap) {
      setErrorMessage(
        `You already have a plan queued up. It will activate once your current plan finishes.`
      );
      return;
    }

    const userId = sessionStorage.getItem("userId");
    const token = sessionStorage.getItem("token");
    const userName = (sessionStorage.getItem("userName") || "").trim() || "Customer";
    const userEmail = sessionStorage.getItem("email");

    try {
      const orderResponse = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_API_URL}subscription/create-order`,
        data: {
          amount: price,
          userId,
          plan: planName,
          meals: mealCount,
          mealType,
          carbType,
          lunchDinner,
          mealStartDate,
          allergy,
          spiceLevel: taste.spiceLevel,
          flavourPreferences: taste.flavourPreferences,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const options = {
        key: orderResponse.data.keyId,
        amount: orderResponse.data.amount,
        currency: orderResponse.data.currency,
        name: "Meal Subscription",
        description: `Subscribe to ${planName}`,
        order_id: orderResponse.data.orderId,
        handler: async function (response) {
          try {
            const verifyResponse = await axios({
              method: "POST",
              url: `${process.env.REACT_APP_API_URL}subscription/verify-payment`,
              data: {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                userId,
                plan: planName,
                startDate: new Date().toISOString(),
                meals: mealCount,
                mealType,
                carbType,
                lunchDinner,
                mealStartDate,
                allergy,
                spiceLevel: taste.spiceLevel,
                flavourPreferences: taste.flavourPreferences,
              },
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (verifyResponse.status === 201) {
              const isQueued = verifyResponse.data?.isQueued || false;

              if (isQueued) {
                // Notification to Customer — queued plan
                const queuedTpl = getQueuedEmailTemplate(planName, userName);
                sendEmail(userId, "", queuedTpl.subject, queuedTpl.text);

                // Notification to Admin
                sendEmail("ermoinzafarsheikh@hotmail.com", "Admin", `Queued Subscription: ${userName}`, `A new subscription has been queued!\n
--- CUSTOMER DETAILS ---
Name: ${userName}\nEmail: ${userEmail}\nUser ID: ${userId}\n
--- QUEUED SUBSCRIPTION DETAILS ---
Plan: ${planName}\nMeals Total: ${mealCount}\nMeal Type: ${mealType}\nCarb Type: ${carbType}\nPreference: ${lunchDinner}\nStart Date: ${mealStartDate}\nAllergy Info: ${allergy || "None"}\nTaste Preference: ${formatTastePreference(taste) || "Not specified"}\n
This plan is QUEUED and will activate when the user's current plan runs out.`);

                setHasQueuedPlan(true);
                setNextPlan({
                  plan: planName,
                  totalMeals: mealCount,
                  mealType,
                  carbType,
                  status: 'queued'
                });
                setTimeout(() => {
                  window.location.href = "/dashboard/my-billing";
                }, 3000);
              } else {
                // Notification to Customer — active plan
                const activeTpl = getActiveEmailTemplate(planName, userName);
                sendEmail(userId, "", activeTpl.subject, activeTpl.text);

                // Detailed Notification to Admin
                sendEmail("ermoinzafarsheikh@hotmail.com", "Admin", `New Subscription: ${userName}`, `A new subscription has been received!\n
--- CUSTOMER DETAILS ---
Name: ${userName}\nEmail: ${userEmail}\nUser ID: ${userId}\n
--- SUBSCRIPTION DETAILS ---
Plan: ${planName}\nMeals Total: ${mealCount}\nMeal Type: ${mealType}\nCarb Type: ${carbType}\nPreference: ${lunchDinner}\nStart Date: ${mealStartDate}\nAllergy Info: ${allergy || "None"}\nTaste Preference: ${formatTastePreference(taste) || "Not specified"}\n
Please update the system accordingly.`);

                setIsSubscribed(true);
                setCurrentPlan({
                  plan: planName,
                  subscriptionStartDate: mealStartDate,
                  subscriptionEndDate: new Date().setDate(
                    new Date().getDate() + (planName === "Weekly Plan" ? 7 : 30)
                  ),
                });
                setTimeout(() => {
                  window.location.href = "/dashboard";
                }, 5000);
              }
            }
          } catch (error) {
            console.error("Payment verification failed:", error);
            setErrorMessage("Payment verification failed. Please try again.");
          }
        },
        prefill: {
          name: userName,
          email: userEmail,
        },
        theme: {
          color: "#3C9B62",
        },
        modal: {
          ondismiss: function () {
            setErrorMessage("Payment cancelled. Please try again.");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

      rzp.on("payment.failed", function (response) {
        setErrorMessage("Payment failed. Please try again.");
      });
    } catch (error) {
      console.error("Error initiating payment:", error);
      setErrorMessage("Unable to initiate payment. Please try again later.");
    }
  };

  // isSubscribedTo: returns true if this plan is any one of the currently ACTIVE
  // plans (a user can now have more than one active at once)
  const isSubscribedTo = (planName) => {
    return currentPlans.some(
      (p) =>
        p.plan === planName &&
        p.status !== "queued" &&
        (p?.totalAvailableMeals ?? p?.totalMeals ?? 0) > 0
    );
  };

  // isQueuedTo: returns true if this plan is saved as the next (queued) plan
  const isQueuedTo = (planName) => {
    return nextPlan && nextPlan.plan === planName;
  };

  return {
    isSubscribed,
    handleSubscribe,
    isSubscribedTo,
    isQueuedTo,
    currentPlan,
    currentPlans,
    nextPlan,
    hasQueuedPlan,
    cancellations,
    holidays,
    isLoading,
  };
};

export default useSubscription;
